import { Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { serializeDateRange } from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import { displayToastSuccess } from '@/helpers';
import {
  CursorContextMenu,
  CursorContextMenuPosition,
} from '@/modules/common/components/molecules/context-menu/cursor-context-menu';
import { AuthenticatedRoutes } from '@/router/routes';
import { logger } from '@/utils/logger/logger';

import {
  OVERHEADS_FILTER_KEY_PERIOD,
  OVERHEADS_FILTER_KEY_SEARCH,
} from '../constants/overheads-filter-keys';
import { OverheadListItem } from '../types/overhead';

const TAG = 'OverheadsRowContextMenu:';

export interface OverheadsRowContextMenuState extends CursorContextMenuPosition {
  overhead: OverheadListItem;
}

interface OverheadsRowContextMenuProps {
  state: OverheadsRowContextMenuState | null;
  onClose: () => void;
}

/**
 * Builds a shareable URL whose filters resolve to exactly this overhead:
 * the name as the search string + the overhead's date as the period.
 */
const buildOverheadLink = (overhead: OverheadListItem): string => {
  const date = new Date(overhead.date);
  const params = new URLSearchParams({
    [OVERHEADS_FILTER_KEY_SEARCH]: overhead.name,
    [OVERHEADS_FILTER_KEY_PERIOD]: serializeDateRange({ startDate: date, endDate: date }),
  });
  return `${window.location.origin}${AuthenticatedRoutes.Overheads}?${params.toString()}`;
};

/** Right-click menu for overhead rows — copy a filtered deep link to the entry. */
export const OverheadsRowContextMenu = ({ state, onClose }: OverheadsRowContextMenuProps) => {
  const { t } = useTranslation('authenticated.overheads');

  const handleCopyLink = async () => {
    if (!state) return;
    try {
      await navigator.clipboard.writeText(buildOverheadLink(state.overhead));
      displayToastSuccess(t('table.copyLinkSuccess'));
    } catch (error) {
      logger.error(TAG, 'Failed to copy overhead link', { error });
    }
  };

  return (
    <CursorContextMenu
      position={state}
      onClose={onClose}
      items={[
        {
          key: 'copy-link',
          label: t('table.copyLink'),
          icon: <Link2 size={16} />,
          onSelect: handleCopyLink,
        },
      ]}
    />
  );
};
